import { portalLocale, portalText, usePortalTranslation } from "../i18n/portal.js";
/**
 * The scope guard — what the console does when its organization or workspace changes (plan-014 A4,
 * ED-12, design §27.6.4 and §27.10.18, FR12-AUTH-11).
 *
 * One provider, mounted by the shell, owns the whole sequence so no page has to know it exists:
 *
 *   **switching here** (`useSwitchScope`)  ask the other tabs whether any holds unsaved edits (and
 *     this tab), and ask the person first if so → `POST /v1/session/account` under the current
 *     generation → tell the other tabs → close the previous scope (below) → open the new context's
 *     landing, with "Switched to {name}. Your previous organization's open pages and drafts were
 *     closed." when the organization changed;
 *   **switched elsewhere** (another tab's broadcast, or a `409 scope_changed` refusal, both arriving
 *     as `ScopeGeneration`'s `left`)  close the previous scope → re-read the auth_session (the one
 *     request a stale tab may still make) → open its landing, with the same notice.
 *
 * "Close the previous scope" is, in this order: `queryClient.cancelQueries()` then `clear()` (no
 * figure of the previous scope survives, and none still in flight can land — the gateway also drops a
 * late answer, `./scoped-fetch.ts`), every stream closed (the gateway does it on the generation
 * change, `../api/stream.ts`), and every scope-bound draft dropped (`./draft.ts`).
 */
import { useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@wetalk/api-client";
import { useNavigate } from "@tanstack/react-router";
import {
  createContext,
  use,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { useGateway } from "../api/provider.js";
import { QUERY_KEY, authContextQuery } from "../api/query.js";
import type { AuthSessionView, ScopeTargetView } from "../api/gateway.js";
import { browserScopePort, createScopeBroadcast, SCOPE_DIRTY_WINDOW_MS, type ScopeBroadcast } from "./broadcast.js";
import { dropScopeDrafts } from "./draft.js";
import { rememberLastUsedWorkspace } from "./last-used.js";
import { recordAccountSwitchError } from "./switch-error.js";
import { landingPathOf } from "./target.js";

/** What the post-switch notice says, decided when the switch completes (names are the NEW scope's). */
export type ScopeNotice =
  | { readonly kind: "organization_switched"; readonly accountName: string }
  | { readonly kind: "workspace_switched_elsewhere"; readonly workspaceName: string }
  | { readonly kind: "workspace_archived"; readonly destinationName: string }
  | { readonly kind: "account_suspended"; readonly accountName: string }
  // plan-014 FIX2: `GET /v1/session` moved the auth_session out of a workspace or organization the person was removed
  // from (`notice: membership_revoked`) — to the next one they can open, or to the picker when none is left.
  | { readonly kind: "membership_revoked"; readonly onPicker: boolean }
  // 2026-10-15 (owner report): the organization this person closed from this tab — once its access ended, the move
  // that follows says so in place of "You no longer have access to that workspace".
  | { readonly kind: "organization_closed"; readonly accountName: string; readonly reopenUntil: string | null };

/** The notice's words. The organization sentence is FR12-AUTH-11's, verbatim. */
export function scopeNoticeText(notice: ScopeNotice): string {
  switch (notice.kind) {
    case "organization_switched":
      return portalText("shell", "switched_to_value_your_previous_organization_s_open_pages_360c5464", { v0: String(notice.accountName) });
    case "workspace_switched_elsewhere":
      return portalText("shell", "switched_to_value_in_another_tab_this_tab_s_open_pages_and_95ab4ac8", { v0: String(notice.workspaceName) });
    case "workspace_archived":
      return portalText("shell", "the_workspace_you_were_in_was_archived_so_you_are_now_in_v_fe6e2683", { v0: String(notice.destinationName) });
    case "account_suspended":
      return portalText("shell", "value_is_suspended_its_finance_pages_stay_open_so_it_can_b_b15af117", { v0: String(notice.accountName) });
    case "membership_revoked":
      return notice.onPicker
        ? portalText("shell", "you_no_longer_have_access_to_that_workspace_choose_where_t_3004aa1d")
        : portalText("shell", "you_no_longer_have_access_to_that_workspace_we_ve_taken_yo_43675842");
    case "organization_closed":
      return notice.reopenUntil === null
        ? portalText("shell", "value_is_closed_access_has_ended_for_everyone_reopen_from_7c41d2e0", { v0: notice.accountName })
        : portalText("shell", "value_is_closed_access_has_ended_for_everyone_until_value_b83f5a19", {
            v0: notice.accountName,
            v1: new Intl.DateTimeFormat(portalLocale() === "el" ? "el-GR" : "en-GB", { day: "numeric", month: "long", year: "numeric" }).format(new Date(notice.reopenUntil)),
          });
  }
}

/** How long an announced closure waits for the move its access ending causes (the worker's pass is every minute). */
export const CLOSED_ORGANIZATION_NOTICE_WAIT_MS = 15 * 60 * 1000;

/** The new scope's name for a notice: the organization's, and its workspace's where one is open. */
function scopeNameOf(authSession: AuthSessionView): { readonly account: string | null; readonly workspace: string | null } {
  if (authSession.kind === "selection_required") return { account: null, workspace: null };
  return {
    account: authSession.accountName,
    // The name is the fresh access listing's: the sign-in names its workspace by id only (review #150).
    workspace:
      authSession.kind === "workspace_selected"
        ? (authSession.accountChoice
            .find((one) => one.accountId === authSession.accountId)
            ?.workspace.find((one) => one.workspaceId === authSession.workspaceId)?.name ?? null)
        : null,
  };
}

/** The notice for a move from `before` to `after`, or `null` when nothing a person needs to hear changed. */
export function noticeOfMove(
  before: AuthSessionView | null | undefined,
  after: AuthSessionView,
  elsewhere: boolean,
): ScopeNotice | null {
  const was = before === null || before === undefined || before.kind === "selection_required" ? null : before.accountId;
  const now = scopeNameOf(after);
  const nowAccountId = after.kind === "selection_required" ? null : after.accountId;
  if (now.account !== null && was !== null && nowAccountId !== was) {
    return { kind: "organization_switched", accountName: now.account };
  }
  if (elsewhere && now.workspace !== null) return { kind: "workspace_switched_elsewhere", workspaceName: now.workspace };
  return null;
}

/** A switch waiting for the person to confirm, because a tab holds unsaved edits. */
export interface PendingSwitch {
  readonly target: ScopeTargetView;
  /** Other tabs that answered `dirty`. */
  readonly dirtyTab: number;
  /** Whether THIS tab holds unsaved edits. */
  readonly dirtyHere: boolean;
}

interface ScopeGuardValue {
  readonly isDirty: () => boolean;
  readonly markDirty: (token: string, dirty: boolean) => void;
  readonly notice: ScopeNotice | null;
  readonly showNotice: (notice: ScopeNotice | null) => void;
  /** Closes the previous scope: cancel and clear every query, drop every scope-bound draft. */
  readonly closePreviousScope: () => Promise<void>;
  readonly switchScope: SwitchScope;
  /** Announces the organization this tab closed: the move out of it, once its access ends, says so. */
  readonly expectClosedOrganization: (notice: Extract<ScopeNotice, { kind: "organization_closed" }>) => void;
  /** The notice a move out of a revoked scope earns: the announced closure while it waits, else "no longer have access". */
  readonly revokedMoveNotice: (onPicker: boolean) => ScopeNotice;
}

const ScopeGuardContext = createContext<ScopeGuardValue | null>(null);

function useScopeGuardValue(): ScopeGuardValue {
  const found = use(ScopeGuardContext);
  if (found === null) throw new Error("No scope guard in context. The shell mounts <ScopeGuard> above every page.");
  return found;
}

function browserTimer(run: () => void, ms: number): () => void {
  const timer = setTimeout(run, ms);
  return () => {
    clearTimeout(timer);
  };
}

export function ScopeGuard({ children }: { readonly children: ReactNode }): ReactNode {
  usePortalTranslation("shell");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const dirty = useRef(new Set<string>());
  // A ref, not state: the channel is an external resource the effect below owns, read only by
  // event handlers (a switch), so nothing renders from it.
  const broadcastRef = useRef<ScopeBroadcast | null>(null);
  const [notice, setNotice] = useState<ScopeNotice | null>(null);
  const closedOrganization = useRef<{ readonly notice: Extract<ScopeNotice, { kind: "organization_closed" }>; readonly atMs: number } | null>(null);
  const expectClosedOrganization = useCallback((announced: Extract<ScopeNotice, { kind: "organization_closed" }>) => {
    closedOrganization.current = { notice: announced, atMs: Date.now() };
  }, []);
  const revokedMoveNotice = useCallback((onPicker: boolean): ScopeNotice => {
    const announced = closedOrganization.current;
    if (announced !== null && Date.now() - announced.atMs <= CLOSED_ORGANIZATION_NOTICE_WAIT_MS) return announced.notice;
    closedOrganization.current = null;
    return { kind: "membership_revoked", onPicker };
  }, []);

  const isDirty = useCallback(() => dirty.current.size > 0, []);
  const markDirty = useCallback((token: string, isNowDirty: boolean) => {
    if (isNowDirty) dirty.current.add(token);
    else dirty.current.delete(token);
  }, []);

  const closePreviousScope = useCallback(async () => {
    await queryClient.cancelQueries();
    queryClient.clear();
    dropScopeDrafts();
  }, [queryClient]);

  // The channel lives as long as the shell does (created in an effect, so a double-mounted
  // component in development does not leave a second channel open).
  useEffect(() => {
    const created = createScopeBroadcast({
      port: browserScopePort(),
      generation: () => gateway.scopeGeneration.current(),
      isDirty,
      setTimer: browserTimer,
      dirtyWindowMs: SCOPE_DIRTY_WINDOW_MS,
    });
    broadcastRef.current = created;
    const stopListening = created.onChanged((generation) => {
      gateway.scopeGeneration.noteLeft(generation);
    });
    return () => {
      stopListening();
      created.close();
      broadcastRef.current = null;
    };
  }, [gateway, isDirty]);

  // Switched elsewhere: close the previous scope, re-read the auth_session, open its landing.
  useEffect(
    () =>
      gateway.scopeGeneration.onChange((event) => {
        if (event.kind !== "left") return;
        const before = queryClient.getQueryData<AuthSessionView | null>(QUERY_KEY.authSession);
        void (async () => {
          await closePreviousScope();
          try {
            const after = await queryClient.fetchQuery(authContextQuery(gateway));
            if (after === null) {
              await navigate({ to: "/log-in" });
              return;
            }
            // Navigate first: the notice belongs to the page it lands on, and retires on the next.
            await navigate({ to: landingPathOf(after) ?? "/" });
            setNotice(noticeOfMove(before, after, true));
          } catch (raised: unknown) {
            // The re-read failed (a network fault): the shell's own read of the auth_session shows
            // the failure with Try again (`shell-failure.tsx`); nothing of the old scope is left to show.
            recordAccountSwitchError(raised);
          }
        })();
      }),
    [gateway, queryClient, navigate, closePreviousScope],
  );

  // Switching here — one state for the whole console, so the shell draws one confirmation and one
  // problem line whichever control asked (a picker, or a Workspaces card).
  const [pending, setPending] = useState<(PendingSwitch & { readonly notice: ScopeNotice | undefined }) | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<unknown>(null);

  const perform = useCallback(
    async (target: ScopeTargetView, forced: ScopeNotice | undefined): Promise<void> => {
      setBusy(true);
      setProblem(null);
      const before = queryClient.getQueryData<AuthSessionView | null>(QUERY_KEY.authSession);
      try {
        const switched = await gateway.switchScope(target);
        broadcastRef.current?.announceChanged(switched.authSession.scopeGeneration);
        await closePreviousScope();
        queryClient.setQueryData<AuthSessionView | null>(QUERY_KEY.authSession, switched.authSession);
        if (switched.authSession.kind === "workspace_selected") {
          rememberLastUsedWorkspace(
            switched.authSession.person.personId,
            switched.authSession.accountId,
            switched.authSession.workspaceId,
          );
        }
        // Navigate first: the notice belongs to the page it lands on, and retires on the next.
        await navigate({ to: landingPathOf(switched.authSession) ?? "/" });
        setNotice(forced ?? noticeOfMove(before, switched.authSession, false));
        // An announced closure is said once, by the move its access ending caused (or by the picker it left the person on).
        closedOrganization.current = null;
      } catch (raised: unknown) {
        recordAccountSwitchError(raised);
        setProblem(raised);
      } finally {
        setBusy(false);
      }
    },
    [gateway, queryClient, navigate, closePreviousScope],
  );

  const switchScope = useMemo<SwitchScope>(
    () => ({
      switchTo: (target, forced) => {
        if (busy) return;
        void (async () => {
          const cached = queryClient.getQueryData<AuthSessionView | null>(QUERY_KEY.authSession);
          const kind = cachedAccountOf(cached) === target.accountId ? "workspace" : "account";
          const broadcast = broadcastRef.current;
          const { dirtyTab } = broadcast === null ? { dirtyTab: 0 } : await broadcast.announceIntent({ kind });
          const dirtyHere = isDirty();
          if (dirtyTab > 0 || dirtyHere) {
            setPending({ target, dirtyTab, dirtyHere, notice: forced });
            return;
          }
          await perform(target, forced);
        })();
      },
      pending,
      confirm: () => {
        if (pending === null) return;
        setPending(null);
        void perform(pending.target, pending.notice);
      },
      cancel: () => {
        setPending(null);
      },
      busy,
      problem,
    }),
    [busy, pending, problem, isDirty, perform, queryClient],
  );

  const value = useMemo<ScopeGuardValue>(
    () => ({ isDirty, markDirty, notice, showNotice: setNotice, closePreviousScope, switchScope, expectClosedOrganization, revokedMoveNotice }),
    [isDirty, markDirty, notice, closePreviousScope, switchScope, expectClosedOrganization, revokedMoveNotice],
  );
  return <ScopeGuardContext value={value}>{children}</ScopeGuardContext>;
}

/**
 * The seam every customer page uses (design §27.10.18): the generation it is drawn under, and
 * `markDirty(true)` while it holds unsaved edits — a switch in any tab then asks first. A page that
 * unmounts is no longer dirty.
 */
export function useScopeGuard(): { readonly generation: number | null; readonly markDirty: (dirty: boolean) => void } {
  const guard = useScopeGuardValue();
  const gateway = useGateway();
  const token = useId();
  useEffect(
    () => () => {
      guard.markDirty(token, false);
    },
    [guard, token],
  );
  return {
    generation: gateway.scopeGeneration.current(),
    markDirty: (isNowDirty) => {
      guard.markDirty(token, isNowDirty);
    },
  };
}

/** The post-switch notice, for the shell to draw, and its dismissal. */
export function useScopeNotice(): { readonly notice: ScopeNotice | null; readonly dismiss: () => void; readonly show: (notice: ScopeNotice) => void } {
  const { notice, showNotice } = useScopeGuardValue();
  const dismiss = useCallback(() => {
    showNotice(null);
  }, [showNotice]);
  return { notice, dismiss, show: showNotice };
}

/**
 * The organization closed from this tab (2026-10-15): `expect` announces it while access is still ending; `revokedMoveNotice`
 * is what the move out of a revoked scope says — the closure while it is announced, else "no longer have access".
 */
export function useClosedOrganizationNotice(): {
  readonly expect: (notice: Extract<ScopeNotice, { kind: "organization_closed" }>) => void;
  readonly revokedMoveNotice: (onPicker: boolean) => ScopeNotice;
} {
  const { expectClosedOrganization, revokedMoveNotice } = useScopeGuardValue();
  return useMemo(() => ({ expect: expectClosedOrganization, revokedMoveNotice }), [expectClosedOrganization, revokedMoveNotice]);
}

export interface SwitchScope {
  /**
   * Switches to `target`. Asks the other tabs first; when any tab (or this one) holds unsaved edits
   * the switch waits in `pending` for {@link SwitchScope.confirm} or {@link SwitchScope.cancel}.
   * `notice` overrides the notice the move would otherwise earn (an archived workspace, say).
   */
  readonly switchTo: (target: ScopeTargetView, notice?: ScopeNotice) => void;
  readonly pending: PendingSwitch | null;
  readonly confirm: () => void;
  readonly cancel: () => void;
  readonly busy: boolean;
  /** The last failure, raised as it came (an `ApiError` carries the API's code and words), or `null`. */
  readonly problem: unknown;
}

/** The one way every control switches organization or workspace — the shell's pickers and the Workspaces page. */
export function useSwitchScope(): SwitchScope {
  return useScopeGuardValue().switchScope;
}

/** The organization the cached context is in, for deciding whether a switch is a workspace move. */
function cachedAccountOf(cached: AuthSessionView | null | undefined): string | null {
  return cached === null || cached === undefined || cached.kind === "selection_required" ? null : cached.accountId;
}

/** A failed switch in plain words: the API's own for a refusal it explains, ours for the ones it codes. */
export function switchProblemText(raised: unknown): string {
  if (raised instanceof ApiError) {
    if (raised.code === "assurance_required") {
      return portalText("shell", "that_organization_asks_for_a_stronger_sign_in_than_this_on_8df89bd9");
    }
    if (raised.code === "impersonation_scope_fixed") return portalText("shell", "a_wetalk_operator_acting_for_you_cannot_switch_organizatio_fbdc6aea");
    if (raised.code === "scope_changed") {
      return portalText("shell", "this_tab_was_switched_in_another_tab_a_moment_ago_it_has_b_ae9e1e99");
    }
    return raised.message;
  }
  return raised instanceof Error ? raised.message : portalText("shell", "that_organization_or_workspace_could_not_be_opened_61f2825f");
}
